 

 
import { useState } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtINR = (n) => (n != null && n !== 0 ? `Rs.${Number(n).toLocaleString("en-IN")}` : "-");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "-");
const fmtList = (arr) => (Array.isArray(arr) ? arr.join(", ") : arr || "-");
const safe = (v) => v || "-";

const buildLabel = (filters) => {
  if (filters.fromDate && filters.toDate)
    return `Date Range: ${filters.fromDate}  to  ${filters.toDate}`;
  if (filters.month && filters.year)
    return `Month: ${new Date(0, filters.month - 1).toLocaleString("default", { month: "long" })} ${filters.year}`;
  if (filters.year) return `Year: ${filters.year}`;
  if (filters.month)
    return `Month: ${new Date(0, filters.month - 1).toLocaleString("default", { month: "long" })}`;
  if (filters.location) return `Location: ${filters.location}`;
  return "All Records";
};

// ─── Dynamically load jsPDF from CDN ─────────────────────────────────────────
const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });

const ensureJsPDF = async () => {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
};

// ─── COLORS ──────────────────────────────────────────────────────────────────
const C = {
  primary:   [21, 101, 192],   // #1565C0
  green:     [46, 125, 50],    // #2e7d32
  orange:    [230, 81, 0],     // #e65100
  purple:    [123, 31, 162],   // #7b1fa2
  grey:      [85, 85, 85],
  lightBlue: [227, 242, 253],
  lightGreen:[232, 245, 233],
  lightOrange:[255,243,224],
  white:     [255, 255, 255],
  header:    [13, 27, 42],     // #0d1b2a
  rowAlt:    [250, 251, 255],
  due:       [255, 248, 245],
};

// ─── PDF GENERATOR ────────────────────────────────────────────────────────────
const generatePDF = async (data, filters) => {
  await ensureJsPDF();
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const label  = buildLabel(filters);
  const today  = new Date().toLocaleDateString("en-IN");
  const filename = `SoldBikes_${new Date().toISOString().split("T")[0]}.pdf`;

  // ── REUSABLE HEADER / FOOTER ───────────────────────────────────────────────
  const drawPageHeader = (title) => {
    // Dark top bar
    doc.setFillColor(...C.header);
    doc.rect(0, 0, PW, 40, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...C.white);
    doc.text("Sold Bikes Report", 30, 26);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(180, 200, 220);
    doc.text(label, 30, 38);

    // Section title bar
    doc.setFillColor(...C.primary);
    doc.rect(0, 42, PW, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...C.white);
    doc.text(title, 30, 57);

    // Right-aligned meta
    doc.setFontSize(8);
    doc.setTextColor(200, 220, 255);
    doc.text(`Generated: ${today}  |  Records: ${data.length}`, PW - 30, 57, { align: "right" });

    return 72; // y after header
  };

  const drawFooter = () => {
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFillColor(240, 244, 248);
      doc.rect(0, PH - 20, PW, 20, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...C.grey);
      doc.text("Bike Showroom Management System  |  Confidential", 30, PH - 7);
      doc.text(`Page ${i} of ${pages}`, PW - 30, PH - 7, { align: "right" });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE 1 — SUMMARY DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════
  let y = drawPageHeader("Executive Summary");

  const locations = [...new Set(data.map((b) => b.location || "Other"))];
  const totalRev  = data.reduce((s, b) => s + (b.salePrice || 0), 0);
  const totalORP  = data.reduce((s, b) => s + (b.orp || 0), 0);
  const totalDue  = data.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0);
  const dueCount  = data.filter((b) => b.due?.status === "DUE").length;
  const paidCount = data.length - dueCount;

  // KPI cards
  const kpis = [
    { label: "Total Bikes Sold", value: data.length,          color: C.primary },
    { label: "ExShowroom Revenue", value: fmtINR(totalRev),   color: C.green },
    { label: "ORP Revenue",      value: fmtINR(totalORP),     color: C.purple },
    { label: "Pending Due",      value: fmtINR(totalDue),     color: C.orange },
    { label: "Paid",             value: `${paidCount} bikes`,  color: C.green },
    { label: "Due",              value: `${dueCount} bikes`,   color: C.orange },
  ];

  const cardW = (PW - 60) / 3;
  const cardH = 52;
  const cardPad = 10;

  kpis.forEach((k, idx) => {
    const col = idx % 3;
    const row = Math.floor(idx / 3);
    const x   = 30 + col * (cardW + cardPad);
    const cy  = y + row * (cardH + 8);

    doc.setFillColor(...C.white);
    doc.setDrawColor(...k.color);
    doc.setLineWidth(1.2);
    doc.roundedRect(x, cy, cardW, cardH, 4, 4, "FD");

    // Colored left accent
    doc.setFillColor(...k.color);
    doc.roundedRect(x, cy, 5, cardH, 2, 2, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...C.grey);
    doc.text(k.label, x + 14, cy + 17);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...k.color);
    doc.text(String(k.value), x + 14, cy + 38);
  });

  y += 2 * (cardH + 8) + 20;

  // Location breakdown table
  const locHeaders = [
    ["Location", "Bikes", "CASH", "FINANCE", "EX+FIN", "CASH+EX", "PAID", "DUE",
     "ExShowroom (Rs.)", "ORP (Rs.)", "Finance Amt (Rs.)", "Due Remaining (Rs.)"]
  ];
  const locBody = locations.map((loc) => {
    const bikes = data.filter((b) => (b.location || "Other") === loc);
    return [
      loc,
      bikes.length,
      bikes.filter((b) => b.saleType === "CASH").length,
      bikes.filter((b) => b.saleType === "FINANCE").length,
      bikes.filter((b) => b.saleType === "EX+FIN").length,
      bikes.filter((b) => b.saleType === "CASH+EX").length,
      bikes.filter((b) => b.due?.status !== "DUE").length,
      bikes.filter((b) => b.due?.status === "DUE").length,
      fmtINR(bikes.reduce((s, b) => s + (b.salePrice || 0), 0)),
      fmtINR(bikes.reduce((s, b) => s + (b.orp || 0), 0)),
      fmtINR(bikes.reduce((s, b) => s + (b.finance?.amount || 0), 0)),
      fmtINR(bikes.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0)),
    ];
  });

  // Grand total row
  locBody.push([
    "GRAND TOTAL", data.length,
    data.filter((b) => b.saleType === "CASH").length,
    data.filter((b) => b.saleType === "FINANCE").length,
    data.filter((b) => b.saleType === "EX+FIN").length,
    data.filter((b) => b.saleType === "CASH+EX").length,
    paidCount, dueCount,
    fmtINR(totalRev), fmtINR(totalORP),
    fmtINR(data.reduce((s, b) => s + (b.finance?.amount || 0), 0)),
    fmtINR(totalDue),
  ]);

  doc.autoTable({
    startY: y,
    head: locHeaders,
    body: locBody,
    styles:       { font: "helvetica", fontSize: 7.5, cellPadding: 4 },
    headStyles:   { fillColor: C.primary, textColor: C.white, fontStyle: "bold", halign: "center" },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 72 },
      8: { halign: "right" }, 9:  { halign: "right" },
      10:{ halign: "right" }, 11: { halign: "right", textColor: [230, 81, 0] },
    },
    didParseCell: ({ row, cell }) => {
      if (row.index === locBody.length - 1) {
        cell.styles.fillColor  = [227, 242, 253];
        cell.styles.fontStyle  = "bold";
        cell.styles.textColor  = C.primary;
      }
    },
    margin: { left: 30, right: 30 },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE 2+ — FULL BIKE LIST (grouped by location)
  // ═══════════════════════════════════════════════════════════════════════════
  const LOC_COLOR_MAP = {
    Khejuri:      C.primary,
    Chaitanyapur: C.purple,
    Nandigram:    C.green,
    Other:        C.orange,
  };

  locations.forEach((loc) => {
    const bikes    = data.filter((b) => (b.location || "Other") === loc);
    const locColor = LOC_COLOR_MAP[loc] || C.orange;

    doc.addPage();
    const startY = drawPageHeader(`${loc} — Sold Bikes (${bikes.length})`);

    const mainHead = [[
      "#", "Invoice", "Date", "Model", "Color",
      "Customer", "Mobile", "ExShowroom", "ORP",
      "Sale Type", "Finance Co.", "Finance Amt",
      "Ex.Price", "Due Status", "Remaining"
    ]];

    const mainBody = bikes.map((b, i) => [
      i + 1,
      safe(b.invoiceNo),
      fmtDate(b.saleDate),
      safe(b.model),
      safe(b.color),
      safe(b.customerName),
      fmtList(b.mobile),
      fmtINR(b.salePrice),
      fmtINR(b.orp),
      safe(b.saleType),
      safe(b.finance?.companyName),
      fmtINR(b.finance?.amount),
      fmtINR(b.exchange?.exchangePrice),
      b.due?.status || "PAID",
      b.due?.status === "DUE" ? fmtINR(b.due?.remainingAmount) : "-",
    ]);

    doc.autoTable({
      startY,
      head: mainHead,
      body: mainBody,
      styles:       { font: "helvetica", fontSize: 7, cellPadding: 3 },
      headStyles:   { fillColor: locColor, textColor: C.white, fontStyle: "bold", halign: "center", fontSize: 7.5 },
      alternateRowStyles: { fillColor: C.rowAlt },
      columnStyles: {
        0:  { cellWidth: 18, halign: "center" },
        1:  { cellWidth: 48, textColor: C.primary },
        2:  { cellWidth: 45 },
        3:  { cellWidth: 58, fontStyle: "bold" },
        4:  { cellWidth: 40 },
        5:  { cellWidth: 72 },
        6:  { cellWidth: 62 },
        7:  { cellWidth: 55, halign: "right" },
        8:  { cellWidth: 55, halign: "right" },
        9:  { cellWidth: 38, halign: "center" },
        10: { cellWidth: 48 },
        11: { cellWidth: 50, halign: "right" },
        12: { cellWidth: 50, halign: "right" },
        13: { cellWidth: 32, halign: "center" },
        14: { cellWidth: 52, halign: "right" },
      },
      didParseCell: ({ row, cell, column }) => {
        const bike = bikes[row.index];
        if (!bike) return;
        if (bike.due?.status === "DUE") {
          cell.styles.fillColor = [255, 248, 245];
        }
        // Color Due Status cell
        if (column.index === 13) {
          cell.styles.textColor = bike.due?.status === "DUE" ? C.orange : C.green;
          cell.styles.fontStyle = "bold";
        }
        if (column.index === 14 && bike.due?.status === "DUE") {
          cell.styles.textColor = C.orange;
          cell.styles.fontStyle = "bold";
        }
      },
      margin: { left: 20, right: 20 },
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE — FULL DETAIL (customer + all fields)
  // ═══════════════════════════════════════════════════════════════════════════
  doc.addPage();
  let dy = drawPageHeader("Complete Records — All Fields");

  const detailHead = [[
    "#", "Invoice", "Date", "Location", "Model", "Engine No", "Chasis No",
    "Customer", "Father/CO", "Mobile", "Address", "Village",
    "Discount", "Cash Amt", "RTO", "Insurance",
    "Salesman", "Sold By", "Remarks"
  ]];

  const detailBody = data.map((b, i) => [
    i + 1,
    safe(b.invoiceNo),
    fmtDate(b.saleDate),
    safe(b.location),
    safe(b.model),
    safe(b.engineNumber),
    safe(b.chasisNumber),
    safe(b.customerName),
    safe(b.fatherName),
    fmtList(b.mobile),
    safe(b.address),
    safe(b.village),
    fmtINR(b.discount),
    fmtINR(b.cashAmount),
    fmtINR(b.charges?.rto),
    fmtINR(b.charges?.insurance),
    safe(b.salesman?.name),
    safe(b.soldBy?.name),
    safe(b.remarks),
  ]);

  doc.autoTable({
    startY: dy,
    head: detailHead,
    body: detailBody,
    styles:             { font: "helvetica", fontSize: 6.5, cellPadding: 2.5 },
    headStyles:         { fillColor: C.header, textColor: C.white, fontStyle: "bold", fontSize: 7 },
    alternateRowStyles: { fillColor: C.rowAlt },
    margin:             { left: 20, right: 20 },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PAGE — DUE PAYMENTS ONLY
  // ═══════════════════════════════════════════════════════════════════════════
  const dueData = data.filter((b) => b.due?.status === "DUE");
  if (dueData.length > 0) {
    doc.addPage();
    drawPageHeader(`Due Payments — ${dueData.length} Pending`);

    const dueHead = [[
      "#", "Invoice", "Date", "Location", "Model", "Customer",
      "Mobile", "ExShowroom (Rs.)", "Total Due (Rs.)", "Paid (Rs.)", "Remaining (Rs.)", "Address"
    ]];

    const dueBody = dueData.map((b, i) => [
      i + 1,
      safe(b.invoiceNo),
      fmtDate(b.saleDate),
      safe(b.location),
      safe(b.model),
      safe(b.customerName),
      fmtList(b.mobile),
      fmtINR(b.salePrice),
      fmtINR(b.due?.totalAmount),
      fmtINR(b.due?.paidAmount),
      fmtINR(b.due?.remainingAmount),
      safe(b.address),
    ]);

    // Grand total row
    dueBody.push([
      "", "TOTAL", "", "", "", "",
      "",
      fmtINR(dueData.reduce((s, b) => s + (b.salePrice || 0), 0)),
      fmtINR(dueData.reduce((s, b) => s + (b.due?.totalAmount || 0), 0)),
      fmtINR(dueData.reduce((s, b) => s + (b.due?.paidAmount || 0), 0)),
      fmtINR(dueData.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0)),
      "",
    ]);

    doc.autoTable({
      startY: 72,
      head: dueHead,
      body: dueBody,
      styles:       { font: "helvetica", fontSize: 7.5, cellPadding: 4 },
      headStyles:   { fillColor: C.orange, textColor: C.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: C.due },
      columnStyles: {
        7:  { halign: "right" },
        8:  { halign: "right" },
        9:  { halign: "right", textColor: C.green },
        10: { halign: "right", textColor: C.orange, fontStyle: "bold" },
      },
      didParseCell: ({ row, cell }) => {
        if (row.index === dueBody.length - 1) {
          cell.styles.fillColor = [255, 243, 224];
          cell.styles.fontStyle = "bold";
          cell.styles.textColor = C.orange;
        }
      },
      margin: { left: 30, right: 30 },
    });
  }

  // ── footers + save ────────────────────────────────────────────────────────
  drawFooter();
  doc.save(filename);
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const ExportpdfSell = ({ data = [], filters = {} }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if (!data.length) return;
    setLoading(true);
    try {
      await generatePDF(data, filters);
    } catch (e) {
      console.error("PDF export error:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={`Export ${data.length} records to PDF`} arrow>
      <span>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
          onClick={handleExport}
          disabled={loading || !data.length}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            fontSize: "0.75rem",
            color: "#c62828",
            borderColor: "#c62828",
            "&:hover": { bgcolor: "#fce4ec", borderColor: "#b71c1c" },
          }}
        >
          {loading ? "Generating…" : "Export PDF"}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ExportpdfSell;